import React from 'react';
import {EventType, EventTypeData} from "types/EventType";
import {Form, Formik, FormikHelpers} from "formik";
import {useNavigate} from "react-router-dom";
import IconName from "types/IconName";
import IconSelector from "components/Form/IconSelector";
import Button from "components/Button";
import Field from "components/Form/Field";
import {useOperator} from "context/OperatorContext";
import FieldHeader from "../../../components/Form/FieldHeader";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import {TextAreaField} from "../../../components/Form/Form";

type Props = {
  eventType?: EventType
  onSubmit: (values: EventTypeData, helpers: FormikHelpers<EventTypeData>) => any
}

function EventTypeForm(props: Props) {
  const {termFor} = useOperator();
  const navigate = useNavigate();

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("This field is required").max(255, 'Maximum character limit exceeded'),
    description: Yup.string().nullable().max(255, 'Maximum character limit exceeded'),
    icon: Yup.string().required("This field is required"),
  })

  const initialValues: EventTypeData = {
    name: props.eventType?.name ?? '',
    description: props.eventType?.description ?? '',
    colour: props.eventType?.colour ?? '#2b2d30',
    icon: props.eventType?.icon ?? IconName.CALENDAR
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={props.onSubmit}>
      {formik => (
        <Form className="space-y-8 divide-y divide-gray-200">
          <ErrorNotification formik={formik} />

          <div className="shadow sm:rounded-md">
            <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
              <div className="space-y-8 divide-y divide-gray-200">
                <div>
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Key Information
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Enter basic details about the {termFor('event type')}
                    </p>
                  </div>
                  <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                    <div className="sm:col-span-1">
                      <IconSelector
                        selected={initialValues.icon}
                        onChange={(iconName) => formik.setFieldValue('icon', iconName)}
                      />
                    </div>
                    <div className="sm:col-span-5">
                      <Field
                        type="text"
                        name="name"
                        id="name"
                        label='Name *'
                        hint='255 chars max'
                        autoComplete="name"
                        maxLength="255"
                        autoFocus
                      />
                    </div>

                    <div className="sm:col-span-6">
                      <div className="mt-1">
                        <TextAreaField
                          label='Description'
                          name="description"
                          hint="Optional. 255 chars max."
                          id="description"
                          autoComplete="off"
                          maxChars={255}
                          rows={3}
                        />
                      </div>
                    </div>

                    <div className="col-span-2 sm:col-span-1 sm:col-start-1">
                      <FieldHeader label="Colour"/>
                      <Field
                        type="color"
                        name="colour"
                        id="colour"
                        className="appearance-none w-full h-14 -ml-0.5 bg-transparent border-0 cursor-pointer"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* TODO: Can this be extracted as a component? It's the same on almost every form */}
            <div className="px-4 py-5 bg-gray-50 text-right sm:px-6 sm:rounded-b-md">
              <div className="flex space-x-3 justify-end">
                <Button
                  color={Button.colors.WHITE}
                  onClick={() => navigate(-1)}
                  disabled={formik.isSubmitting}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  showSpinner={formik.isSubmitting}
                  disabled={formik.isSubmitting}>
                  Save
                </Button>
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default EventTypeForm;